import { Check } from 'lucide-react';
import { useId, useState } from 'react';

import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Switch } from '@/modules/ui/components/switch/Switch';

interface CandidateNoticeSettingProps {
  title: string;
  description: string;
  enabled: boolean;
  /** The Off Warning the user is bound to when switching off; from the API. */
  offWarning: string | undefined;
  isSaving: boolean;
  error?: string | null;
  /** Set once a switch-off is recorded; shows the confirmation line. */
  isOffRecorded: boolean;
  /** Locks the switch when a setting above this one decides it. */
  disabled?: boolean;
  onChange: (enabled: boolean) => void;
}

/**
 * One candidate notice switch. Switching on applies at once. Switching off
 * first shows the Off Warning inline with a confirm button, like rejecting a
 * candidate, because confirming records the user's acceptance of that warning.
 */
export function CandidateNoticeSetting({
  title,
  description,
  enabled,
  offWarning,
  isSaving,
  error,
  isOffRecorded,
  disabled = false,
  onChange,
}: CandidateNoticeSettingProps) {
  const [isConfirmingOff, setIsConfirmingOff] = useState(false);
  const switchId = useId();

  const change = (checked: boolean) => {
    if (checked) {
      onChange(true);
      return;
    }
    setIsConfirmingOff(true);
  };

  const confirmOff = () => {
    onChange(false);
    setIsConfirmingOff(false);
  };

  return (
    <div className="flex flex-col gap-3 py-4">
      <div className="flex items-start justify-between gap-6">
        <div className="flex flex-col gap-0.5">
          <label className="text-[14px] font-medium" htmlFor={switchId}>
            {title}
          </label>
          <p className="text-muted-foreground text-[13px] leading-[19px]">
            {description}
          </p>
        </div>
        <Switch
          checked={enabled && !isConfirmingOff}
          className="mt-0.5"
          disabled={disabled || isSaving || (isConfirmingOff && !offWarning)}
          id={switchId}
          onCheckedChange={change}
        />
      </div>

      {isConfirmingOff && (
        <div className="animate-in fade-in flex flex-col gap-3 rounded-md border border-neutral-200 p-3 duration-150 motion-reduce:animate-none">
          <p className="text-[13px] leading-[19px]">
            {offWarning ?? 'Loading…'}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              autoFocus
              className="h-8 rounded-md"
              disabled={!offWarning}
              loading={isSaving}
              onClick={confirmOff}
              size="sm"
              variant="destructive"
            >
              Turn off
            </Button>
            <Button
              className="h-8 rounded-md"
              disabled={isSaving}
              onClick={() => setIsConfirmingOff(false)}
              size="sm"
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error && <InlineError>{error}</InlineError>}

      {isOffRecorded && !enabled && !isConfirmingOff && (
        <p
          aria-live="polite"
          className="text-muted-foreground flex items-center gap-2 text-[13px]"
        >
          <Check className="text-semantic-560 size-4" />
          Your decision was recorded.
        </p>
      )}
    </div>
  );
}
